<template>
  <div v-if="showBanner" class="bg-white rounded-lg border border-border">
    <!-- Card Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-border">
      <div class="flex items-center gap-2">
        <Package class="w-4 h-4 text-muted-foreground" />
        <h3 class="text-sm font-semibold text-gray-800">Package Access</h3>
      </div>
      <span
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
        :class="badge.pill"
      >
        <div class="w-1.5 h-1.5 rounded-full" :class="badge.dot"></div>
        {{ badge.label }}
      </span>
    </div>

    <!-- Card Body -->
    <div class="px-6 py-5 space-y-4">
      <!-- Product Info Row -->
      <div class="flex items-start justify-between gap-4">
        <div class="min-w-0">
          <h4 class="font-medium text-gray-900 truncate">{{ title }}</h4>
          <p v-if="productDescription" class="text-sm text-muted-foreground mt-0.5">
            {{ productDescription }}
          </p>
        </div>
        <div v-if="displayAmount" class="text-right shrink-0">
          <div class="text-lg font-semibold text-gray-900">{{ displayAmount }}</div>
          <div class="text-xs text-muted-foreground">
            one-time<template v-if="durationMonths"> · {{ durationMonths }} months</template>
          </div>
        </div>
      </div>

      <!-- Access window (active or scheduled purchase) -->
      <div v-if="windowPurchase" class="grid grid-cols-2 gap-3 px-1 text-sm">
        <div>
          <p class="text-xs text-muted-foreground mb-0.5">
            {{ isActiveAccess ? 'Access Started' : 'Access Starts' }}
          </p>
          <p class="font-medium text-gray-900">{{ formatDate(windowPurchase.starts_at) }}</p>
        </div>
        <div class="text-right">
          <p class="text-xs text-muted-foreground mb-0.5">Access Expires</p>
          <p class="font-medium text-gray-900">{{ expiresLabel }}</p>
        </div>
      </div>

      <!-- Purchase CTA (offer available) -->
      <div
        v-if="showCta"
        class="rounded-lg px-4 py-4 bg-amber-50 flex items-center justify-between gap-4"
      >
        <p class="text-xs text-amber-800 leading-snug">
          Purchase this package for continued platform access after your subscription ends.
        </p>
        <button
          type="button"
          class="shrink-0 text-xs font-medium px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-800 transition-colors"
          @click="goToTuition"
        >
          {{ ctaLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { Package } from 'lucide-vue-next'
import { usePlansStore } from '@/stores/subscription'
import { useSubscriptionAccessStore } from '@/stores/subscriptionAccess'
import { APP_ROUTES } from '@/const/appRoutes'

const router = useRouter()
const plansStore = usePlansStore()
const accessStore = useSubscriptionAccessStore()
const { assignedProduct, activePurchase, unexpiredPurchase, canPurchaseProduct } =
  storeToRefs(plansStore)
const { accessExpiresAt, productName } = storeToRefs(accessStore)

const isActiveAccess = computed(() => Boolean(activePurchase.value?.is_active_access))

/** Paid purchase whose access window hasn't started yet (scheduled after the subscription ends). */
const scheduledPurchase = computed(() =>
  !isActiveAccess.value && unexpiredPurchase.value ? unexpiredPurchase.value : null
)

/** Purchase whose access window is shown in the card. */
const windowPurchase = computed(() => activePurchase.value || scheduledPurchase.value)

const showBanner = computed(
  () => isActiveAccess.value || Boolean(scheduledPurchase.value) || canPurchaseProduct.value
)

const showCta = computed(() => canPurchaseProduct.value)

const badge = computed(() => {
  if (isActiveAccess.value) {
    return {
      label: 'Active',
      pill: 'bg-green-50 text-green-700 ring-1 ring-green-200',
      dot: 'bg-green-500'
    }
  }
  if (scheduledPurchase.value) {
    return {
      label: 'Purchased',
      pill: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
      dot: 'bg-blue-500'
    }
  }
  return {
    label: 'Available',
    pill: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    dot: 'bg-amber-500'
  }
})

const title = computed(() => {
  if (isActiveAccess.value) {
    return productName.value || assignedProduct.value?.name || 'Job Application Support'
  }
  return assignedProduct.value?.name || 'Job Application Support package'
})

const productDescription = computed(() => assignedProduct.value?.description || '')

const displayAmount = computed(() => {
  if (windowPurchase.value?.amount != null) {
    return `$${Number(windowPurchase.value.amount).toFixed(2)}`
  }
  const product = assignedProduct.value
  if (!product) return ''
  return product.formatted_amount || `$${Number(product.amount).toFixed(2)}`
})

const durationMonths = computed(
  () => windowPurchase.value?.product?.duration_months ?? assignedProduct.value?.duration_months
)

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const expiresLabel = computed(() =>
  formatDate(windowPurchase.value?.expires_at || accessExpiresAt.value)
)

const ctaLabel = computed(() => (activePurchase.value ? 'Renew Package' : 'View Package'))

const goToTuition = () => {
  void router.push(APP_ROUTES.TUITION)
}
</script>
